import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type ConversationPeer = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  slug: string | null;
  isAmbassador: boolean;
};

export type ConversationSummary = {
  id: string;
  kind: string;
  subject: string | null;
  contextUrl: string | null;
  lastMessageAt: string;
  lastMessagePreview: string | null;
  lastSenderId: string | null;
  unread: number;
  muted: boolean;
  peers: ConversationPeer[];
};

/** Every thread the signed-in user takes part in, newest first. */
export const listConversations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ConversationSummary[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const userId = context.userId;

    const { data: mine } = await supabaseAdmin
      .from("conversation_participants")
      .select("conversation_id,last_read_at,muted,archived")
      .eq("user_id", userId)
      .eq("archived", false);

    const ids = (mine ?? []).map((row) => row.conversation_id);
    if (!ids.length) return [];

    const [{ data: conversations }, { data: participants }] = await Promise.all([
      supabaseAdmin
        .from("conversations")
        .select("id,kind,subject,context_url,last_message_at,last_message_preview,last_sender_id")
        .in("id", ids)
        .order("last_message_at", { ascending: false }),
      supabaseAdmin
        .from("conversation_participants")
        .select("conversation_id,user_id")
        .in("conversation_id", ids),
    ]);

    const peerIds = Array.from(
      new Set((participants ?? []).map((p) => p.user_id).filter((id) => id !== userId)),
    );
    const { data: profiles } = peerIds.length
      ? await supabaseAdmin
          .from("profiles")
          .select("id,display_name,avatar_url,slug,is_ambassador")
          .in("id", peerIds)
      : { data: [] as Array<Record<string, unknown>> };

    const profileById = new Map(
      (profiles ?? []).map((p) => [
        p['id'] as string,
        {
          id: p['id'] as string,
          displayName: (p['display_name'] as string | null) ?? "BeHot member",
          avatarUrl: (p['avatar_url'] as string | null) ?? null,
          slug: (p['slug'] as string | null) ?? null,
          isAmbassador: Boolean(p['is_ambassador']),
        } satisfies ConversationPeer,
      ]),
    );

    const meta = new Map((mine ?? []).map((row) => [row.conversation_id, row]));

    // Unread = messages after my last_read_at that I did not write.
    const { data: unreadRows } = await supabaseAdmin
      .from("messages")
      .select("conversation_id,sender_id,created_at")
      .in("conversation_id", ids)
      .neq("sender_id", userId)
      .order("created_at", { ascending: false })
      .limit(1000);

    return (conversations ?? []).map((c) => {
      const row = meta.get(c.id);
      const lastRead = row?.last_read_at ?? "1970-01-01T00:00:00Z";
      const unread = (unreadRows ?? []).filter(
        (m) => m.conversation_id === c.id && m.created_at > lastRead,
      ).length;
      return {
        id: c.id,
        kind: c.kind,
        subject: c.subject,
        contextUrl: c.context_url,
        lastMessageAt: c.last_message_at,
        lastMessagePreview: c.last_message_preview,
        lastSenderId: c.last_sender_id,
        unread,
        muted: Boolean(row?.muted),
        peers: (participants ?? [])
          .filter((p) => p.conversation_id === c.id && p.user_id !== userId)
          .map((p) => profileById.get(p.user_id))
          .filter((p): p is ConversationPeer => Boolean(p)),
      };
    });
  });

/** Find a member by @handle, slug or exact display name. */
export const findMemberByHandle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ handle: z.string().trim().min(1).max(80) }).parse(input),
  )
  .handler(async ({ data, context }): Promise<ConversationPeer | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const needle = data.handle.replace(/^@/, "").trim();
    if (!needle) return null;

    const { data: rows } = await supabaseAdmin
      .from("profiles")
      .select("id,display_name,avatar_url,slug,handle,is_ambassador,is_suspended")
      .or(`handle.ilike.${needle},slug.ilike.${needle},display_name.ilike.${needle}`)
      .limit(5);

    const match = (rows ?? []).find((r) => r.id !== context.userId && !r.is_suspended);
    if (!match) return null;
    return {
      id: match.id,
      displayName: match.display_name ?? "BeHot member",
      avatarUrl: match.avatar_url ?? null,
      slug: match.slug ?? match.handle ?? null,
      isAmbassador: Boolean(match.is_ambassador),
    };
  });

/** Open (or reuse) a direct thread with another member. */
export const startConversation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        recipientId: z.string().uuid(),
        subject: z.string().trim().max(140).optional(),
        kind: z.enum(["direct", "service_request", "listing", "offer"]).default("direct"),
        contextUrl: z.string().trim().max(300).optional(),
        firstMessage: z.string().trim().max(4000).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<{ conversationId: string }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const userId = context.userId;
    if (data.recipientId === userId) throw new Error("You cannot message yourself.");

    const { data: recipient } = await supabaseAdmin
      .from("profiles")
      .select("id")
      .eq("id", data.recipientId)
      .maybeSingle();
    if (!recipient) throw new Error("That member does not exist.");

    if (data.kind === "direct") {
      const { data: mine } = await supabaseAdmin
        .from("conversation_participants")
        .select("conversation_id")
        .eq("user_id", userId);
      const myIds = (mine ?? []).map((r) => r.conversation_id);
      if (myIds.length) {
        const { data: shared } = await supabaseAdmin
          .from("conversation_participants")
          .select("conversation_id")
          .eq("user_id", data.recipientId)
          .in("conversation_id", myIds);
        const candidates = (shared ?? []).map((r) => r.conversation_id);
        if (candidates.length) {
          const { data: direct } = await supabaseAdmin
            .from("conversations")
            .select("id")
            .in("id", candidates)
            .eq("kind", "direct")
            .order("last_message_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          if (direct) {
            if (data.firstMessage) {
              await supabaseAdmin
                .from("messages")
                .insert({ conversation_id: direct.id, sender_id: userId, body: data.firstMessage });
            }
            return { conversationId: direct.id };
          }
        }
      }
    }

    const { data: conversation, error } = await supabaseAdmin
      .from("conversations")
      .insert({
        kind: data.kind,
        subject: data.subject ?? null,
        context_url: data.contextUrl ?? null,
        created_by: userId,
      })
      .select("id")
      .single();
    if (error || !conversation) throw error ?? new Error("Could not start that conversation.");

    await supabaseAdmin.from("conversation_participants").insert([
      { conversation_id: conversation.id, user_id: userId },
      { conversation_id: conversation.id, user_id: data.recipientId },
    ]);

    if (data.firstMessage) {
      await supabaseAdmin
        .from("messages")
        .insert({ conversation_id: conversation.id, sender_id: userId, body: data.firstMessage });
    }

    return { conversationId: conversation.id };
  });
